/**
 * Cinema Mode (Mux HLS) — ultra-smooth ~3-5s latency live playback.
 *
 * Used in two roles:
 *   - SELLER:  publishes the camera/mic via WHIP to Mux. Mux re-encodes
 *              to HLS for viewers. Trades ~3s of latency for far smoother
 *              playback than WebRTC, especially over mobile networks.
 *              (live sale platforms uses the same pattern.)
 *   - VIEWER:  loads the HLS playlist from `https://stream.mux.com/<id>.m3u8`
 *              via hls.js (or native Safari HLS), with low-latency HLS
 *              enabled so playback catches up to the broadcaster as fast
 *              as the network allows.
 *
 * The component is intentionally additive — the existing LiveKit/WebRTC
 * mesh path keeps working untouched. The seller toggles Cinema mode on
 * the live show page; toggling off tears down the Mux stream and reverts
 * to the low-latency path.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import Hls from "hls.js";
import { api } from "@/lib/api";

export function CinemaViewer({ playbackUrl, posterUrl }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [ready, setReady] = useState(false);
  // Browsers (Chrome, Safari, Firefox) block autoplay of *unmuted* video
  // without a prior user gesture on the page. To guarantee viewers see
  // the live show the instant they land, we autoplay MUTED, then expose
  // a single-tap "Tap for sound" affordance.  On the user's next tap
  // anywhere on the stage we unmute and the affordance disappears.
  const [needsUnmute, setNeedsUnmute] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !playbackUrl) return undefined;

    video.muted = true;

    // Native HLS (Safari / iOS) — just set src and play.
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = playbackUrl;
      const onLoad = () => setReady(true);
      video.addEventListener("loadeddata", onLoad);
      video.play().catch(() => { /* user gesture required */ });
      return () => video.removeEventListener("loadeddata", onLoad);
    }

    if (!Hls.isSupported()) return undefined;

    const hls = new Hls({
      lowLatencyMode: true,
      enableWorker: true,
      backBufferLength: 30,
    });
    hlsRef.current = hls;
    hls.loadSource(playbackUrl);
    hls.attachMedia(video);

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      setReady(true);
      video.play().catch(() => { /* needs gesture */ });
    });

    hls.on(Hls.Events.ERROR, (_evt, data) => {
      if (!data.fatal) return;
      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR: hls.startLoad(); break;
        case Hls.ErrorTypes.MEDIA_ERROR:   hls.recoverMediaError(); break;
        default: hls.destroy(); hlsRef.current = null;
      }
    });

    return () => {
      hls.destroy();
      hlsRef.current = null;
    };
  }, [playbackUrl]);

  const handleUnmute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = false;
    // Most browsers also pause when the audio context is suspended; force
    // play() inside the user-gesture handler so audio kicks in.
    video.play().catch(() => { /* fine */ });
    setNeedsUnmute(false);
  };

  return (
    <div className="absolute inset-0 bg-zinc-900" data-testid="cinema-viewer">
      <video
        ref={videoRef}
        className="w-full h-full object-cover"
        poster={posterUrl || undefined}
        controls={false}
        autoPlay
        muted
        playsInline
      />
      {!ready && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-zinc-900/70 text-white text-sm font-semibold tracking-wide"
          data-testid="cinema-viewer-loading"
        >
          Loading Cinema mode…
        </div>
      )}
      {ready && needsUnmute && (
        <button
          type="button"
          onClick={handleUnmute}
          className="absolute top-3 left-1/2 -translate-x-1/2 z-20 px-3 py-1.5 rounded-full bg-zinc-900/85 backdrop-blur text-white text-[12px] font-bold tracking-wide border border-white/20 shadow-lg hover:bg-zinc-900"
          data-testid="cinema-viewer-unmute"
        >
          🔇 Tap for sound
        </button>
      )}
    </div>
  );
}

/**
 * Seller-side hook to start / stop a Mux Cinema stream.
 *
 *   const cinema = useCinemaSeller(streamId);
 *   await cinema.start();   // captures camera, publishes via WHIP
 *   await cinema.stop();    // tears down + disables Mux live stream
 */
export function useCinemaSeller(streamId) {
  const [active, setActive] = useState(false);
  const [error, setError] = useState(null);
  const pcRef = useRef(null);
  const streamRef = useRef(null);
  const muxRef = useRef(null);

  const start = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.post(`/mux/streams/${streamId}/start`);
      muxRef.current = data;
      if (!data.whip_url) throw new Error("Mux did not return a WHIP URL");

      const media = await navigator.mediaDevices.getUserMedia({
        // True 4K — phone cameras max at this. Browser drops to the next
        // highest tier the device supports if 2160p isn't available, so
        // this gives us the best the phone can do without crashing.
        video: {
          width:  { ideal: 3840, max: 3840 },
          height: { ideal: 2160, max: 2160 },
          frameRate: { ideal: 30, max: 60 },
        },
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 48000 },
      });
      streamRef.current = media;

      const pc = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
      });
      pcRef.current = pc;
      media.getTracks().forEach((t) => pc.addTrack(t, media));

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const resp = await fetch(data.whip_url, {
        method: "POST",
        headers: { "Content-Type": "application/sdp" },
        body: offer.sdp || "",
      });
      if (!resp.ok) throw new Error(`Mux WHIP rejected: HTTP ${resp.status}`);

      const answerSdp = await resp.text();
      await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
      setActive(true);
      return data;
    } catch (e) {
      setError(e.message || String(e));
      throw e;
    }
  }, [streamId]);

  const stop = useCallback(async () => {
    try { pcRef.current?.getSenders().forEach((s) => s.track?.stop()); } catch (_) { /* ignore */ }
    try { pcRef.current?.close(); } catch (_) { /* ignore */ }
    pcRef.current = null;
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) { /* ignore */ }
    streamRef.current = null;
    try { await api.post(`/mux/streams/${streamId}/end`); } catch (_) { /* server may already be ended */ }
    muxRef.current = null;
    setActive(false);
  }, [streamId]);

  useEffect(() => () => { if (pcRef.current || streamRef.current) stop(); }, [stop]);

  return { active, error, start, stop, mux: muxRef.current };
}
